import { apiClient } from '../../lib/apiClient';
import { createCollectionApi } from '../knowledge-base/knowledgeBaseApi';
import type {
  CampaignCreate,
  CampaignRead,
  CampaignUpdate,
  ContentGoalsRead,
  ContentGoalsUpdate,
  PlannedPostCreate,
  PlannedPostRead,
  PlannedPostUpdate,
} from '../../types/api';

/** Planned posts — intentions on the calendar. Nothing here spends quota. */
export const plannedPostsApi = createCollectionApi<
  PlannedPostRead,
  PlannedPostCreate,
  PlannedPostUpdate
>('planned-posts');

/**
 * A parked (`needs_review`) or failed plan back to `idle`, so the scheduler
 * picks it up again. Any other status answers 409 `PLAN_NOT_RESETTABLE`. The
 * request the last run made stays linked — it is history.
 */
export function resetPlannedPost(businessId: string, id: string): Promise<PlannedPostRead> {
  return apiClient.post<PlannedPostRead>(
    `/api/v1/businesses/${businessId}/planned-posts/${id}/reset`,
  );
}

/**
 * Start an idle automated plan now rather than at its slot: the scheduler's
 * own claim and the same run. A plain plan is 422; one the worker holds 409.
 */
export function runPlannedPostNow(businessId: string, id: string): Promise<PlannedPostRead> {
  return apiClient.post<PlannedPostRead>(
    `/api/v1/businesses/${businessId}/planned-posts/${id}/run-now`,
  );
}

/** Campaigns — the optional colour grouping over planned posts. */
export const campaignsApi = createCollectionApi<CampaignRead, CampaignCreate, CampaignUpdate>(
  'campaigns',
);

/**
 * Goals are a singleton per month, addressed by `YYYY-MM`, so they do not fit
 * the collection shape: there is no list and no id in the path.
 */
function goalsPath(businessId: string, month: string): string {
  return `/api/v1/businesses/${businessId}/content-goals/${month}`;
}

/**
 * Goals for one month. A month nobody has set goals for answers 404 with code
 * `RESOURCE_NOT_FOUND` — an unset month, not a failure. `useContentGoals` reads
 * that code and reports `goals: null`.
 */
export function fetchContentGoals(
  businessId: string,
  month: string,
  signal?: AbortSignal,
): Promise<ContentGoalsRead> {
  return apiClient.get<ContentGoalsRead>(goalsPath(businessId, month), { signal });
}

/** Upsert: the same PUT sets a month's goals for the first time and edits them. */
export function saveContentGoals(
  businessId: string,
  month: string,
  body: ContentGoalsUpdate,
): Promise<ContentGoalsRead> {
  return apiClient.put<ContentGoalsRead>(goalsPath(businessId, month), { body });
}
